import type { components } from 'admination-react/api-schema'
import { fillPath, sendJson } from '@/core/api/http'
import { apiListAll, newestLive } from '@/core/api/resources'

type Schemas = components['schemas']

export type UnterschriftDokument = 'foerderplan' | 'erstbericht'

export type Unterschreibende = 'teilnehmende' | 'coach'

export type Unterschriften = {
  dokumentId: string
  teilnehmende: boolean
  coach: boolean
}

export const DOKUMENT_NAME: Record<UnterschriftDokument, string> = {
  foerderplan: 'Individueller Förderplan',
  erstbericht: 'Erstbericht',
}

const ROLLE: Record<Unterschreibende, readonly string[]> = {
  teilnehmende: ['teilnehmer'],
  coach: ['gegenzeichnung', 'abgabe'],
}

export async function readUnterschriften(
  coachingId: string,
  dokument: UnterschriftDokument,
): Promise<Unterschriften | null> {
  const rows = await apiListAll('/dokumente', {
    coaching_id: coachingId,
    name: DOKUMENT_NAME[dokument],
    include: ['signatur'],
  })
  const row = newestLive(rows)
  if (!row) return null
  if (typeof row.id !== 'string' || row.id === '') {
    throw new Error('Die Antwort des Servers hatte nicht die erwartete Form.')
  }
  const rollen = (row.signatur ?? []).map((s) => s.rolle)
  const hat = (wer: Unterschreibende) => ROLLE[wer].some((r) => rollen.includes(r))
  return { dokumentId: row.id, teilnehmende: hat('teilnehmende'), coach: hat('coach') }
}

type UnterschriftBody = Schemas['UnterschriftRequest']

const UNTERSCHRIFT_PFAD = {
  foerderplan: {
    teilnehmende: '/foerderplan/{coaching_id}/unterschrift/teilnehmende',
    coach: '/foerderplan/{coaching_id}/unterschrift/coach',
  },
  erstbericht: {
    teilnehmende: '/erstbericht/{coaching_id}/unterschrift/teilnehmende',
    coach: '/erstbericht/{coaching_id}/unterschrift/coach',
  },
} as const

export async function unterschreibe(
  coachingId: string,
  dokument: UnterschriftDokument,
  wer: Unterschreibende,
  signaturPngBase64: string,
): Promise<void> {
  const path = fillPath(UNTERSCHRIFT_PFAD[dokument][wer], { coaching_id: coachingId })
  await sendJson<void>(path, 'POST', { signatur_png_base64: signaturPngBase64 } satisfies UnterschriftBody)
}
